import type { FacilityNode } from "@arc/contracts";
import { facilityPathLabel } from "../../facilities/facility-tree.js";

/**
 * The Favourite facilities page's cards (ADR-034 D7) — the caller's favourite ids ∩ the caller's tree: the breadcrumb
 * (the ancestors, the repo's " / " separator), the counts the tree carries, whether the caller may open its manuals
 * (`permission` — null = an ancestor reached only through descendants: the card DRILLS IN instead, plan panel #4).
 */
export interface FavouriteFacilityCard {
  id: number;
  name: string;
  /** The ancestors' names ("Site / Line"), "" for a root. */
  parentLabel: string;
  permission: FacilityNode["permission"];
  categoryCount: number | null;
  manualCount: number | null;
  childCount: number;
}

export function favouriteFacilityCards(tree: readonly FacilityNode[], favourites: ReadonlySet<number>): FavouriteFacilityCard[] {
  const byId = new Map(tree.map((f) => [f.id, { name: f.name, parentId: f.parentId }] as const));
  const children = new Map<number, number>();
  for (const f of tree) if (f.parentId !== null) children.set(f.parentId, (children.get(f.parentId) ?? 0) + 1);
  return tree
    .filter((f) => favourites.has(f.id) && !f.archived)
    .map((f) => ({
      id: f.id,
      name: f.name,
      parentLabel: facilityPathLabel(byId, f.id),
      permission: f.permission,
      categoryCount: f.categoryCount ?? null,
      manualCount: f.manualCount ?? null,
      childCount: children.get(f.id) ?? 0,
    }))
    .sort((a, b) => a.parentLabel.localeCompare(b.parentLabel) || a.name.localeCompare(b.name) || a.id - b.id);
}

/** The legacy's name search over the favourite cards (case-insensitive substring). */
export function filterCards(cards: readonly FavouriteFacilityCard[], term: string): FavouriteFacilityCard[] {
  const q = term.trim().toLocaleLowerCase();
  return q === "" ? [...cards] : cards.filter((c) => c.name.toLocaleLowerCase().includes(q));
}

/**
 * Group by the parent path label (the legacy `SortAllFacilities`). A root has no parent label, so the caller passes
 * the wording for that bucket — this module is pure grouping and has no translator of its own.
 */
export function groupByParent(
  cards: readonly FavouriteFacilityCard[],
  rootLabel: string,
): Array<{ label: string; cards: FavouriteFacilityCard[] }> {
  const groups = new Map<string, FavouriteFacilityCard[]>();
  for (const c of cards) {
    const key = c.parentLabel === "" ? rootLabel : c.parentLabel;
    const list = groups.get(key) ?? [];
    list.push(c);
    groups.set(key, list);
  }
  return [...groups.entries()].map(([label, cs]) => ({ label, cards: cs }));
}
